import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ShieldCheck, Mail, Lock, User, ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../components/ui/sonner';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, loginWithGoogle, error } = useAuth();
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  // Support both old format (object with pathname) and new format (simple string)
  const fromState = location.state?.from;
  const from = typeof fromState === 'string' ? fromState : (fromState?.pathname || '/');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLocalError('');

    try {
      const userData = await login(loginData.email, loginData.password);
      // Show welcome toast
      toast.success(`Bienvenue, ${userData.name || 'cher client'} !`);
      // Use React Router navigation to preserve SPA state
      navigate(from, { replace: true });
    } catch (err) {
      setLocalError(err.message);
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLocalError('');

    if (registerData.password !== registerData.confirmPassword) {
      setLocalError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    if (registerData.password.length < 6) {
      setLocalError('Le mot de passe doit contenir au moins 6 caractères');
      setLoading(false);
      return;
    }

    try {
      const userData = await register(registerData.email, registerData.password, registerData.name);
      // Show welcome toast
      toast.success(`Bienvenue chez FGS, ${userData.name || registerData.name} !`);
      // Use React Router navigation to preserve SPA state
      navigate(from, { replace: true });
    } catch (err) {
      setLocalError(err.message);
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-white mb-4">
            <ShieldCheck className="w-8 h-8 text-fgs-primary" />
            <span className="font-heading text-2xl font-semibold">FGS</span>
          </div>
          <p className="text-white/60 text-sm">Connectez-vous pour accéder à votre compte</p>
        </div>

        {/* Auth Card */}
        <div className="bg-[#0a0a0a] border border-white/5 rounded-2xl p-6 md:p-8">
          {/* Error Display */}
          {(localError || error) && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
              {localError || error}
            </div>
          )}

          {/* Google Login */}
          <Button 
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-12 border-white/10 text-white hover:bg-white/5 rounded-full mb-6"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuer avec Google
          </Button>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/5" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0a0a0a] text-white/50">ou</span>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-black border border-white/10 mb-6 rounded-full p-1">
              <TabsTrigger 
                value="login" 
                className="data-[state=active]:bg-white data-[state=active]:text-black rounded-full"
              >
                Connexion
              </TabsTrigger>
              <TabsTrigger 
                value="register"
                className="data-[state=active]:bg-white data-[state=active]:text-black rounded-full"
              >
                Inscription
              </TabsTrigger>
            </TabsList>

            {/* Login Form */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                    <Input
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      placeholder="votre@email.com"
                      className="pl-10 bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                    <Input
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      placeholder="••••••••"
                      className="pl-10 bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
                      data-testid="login-password"
                    />
                  </div>
                </div>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-white text-black hover:bg-white/90 h-12 font-medium rounded-full"
                  data-testid="login-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Se connecter
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
                
                {/* Forgot Password Link */}
                <div className="text-center pt-2">
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-white/60 hover:text-white transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
              </form>
            </TabsContent>

            {/* Register Form */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Nom complet</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                    <Input
                      type="text"
                      value={registerData.name}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      placeholder="Jean Dupont"
                      className="pl-10 bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
                      data-testid="register-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                    <Input
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      placeholder="votre@email.com"
                      className="pl-10 bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
                      data-testid="register-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                    <Input
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      placeholder="Min. 6 caractères"
                      className="pl-10 bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
                      data-testid="register-password"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                    <Input
                      type="password"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      placeholder="••••••••"
                      className="pl-10 bg-black border-white/10 text-white placeholder:text-white/50 focus:border-white/20"
                      data-testid="register-confirm"
                    />
                  </div>
                </div>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-white text-black hover:bg-white/90 h-12 font-medium rounded-full"
                  data-testid="register-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Créer un compte
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-white/50 mt-6">
          En vous connectant, vous acceptez nos{' '}
          <a href="/cgv" className="text-white/50 hover:text-white">CGV</a>
          {' '}et notre{' '}
          <a href="/privacy" className="text-white/50 hover:text-white">politique de confidentialité</a>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
